import { FormControl, MenuItem, Select, TextField } from "@mui/material"
import type { FC } from "react"
import { Controller, useFormContext } from "react-hook-form"

import type { ProductTestFormValues } from "../../schemas"
import { ProductTestResultPassFail, ProductTestResultYesNo } from "../../types"
import { getProductTestResultPassFailLabel, getProductTestResultYesNoLabel } from "../../utils"

import { FIELD_ERROR_CLASSES, SELECT_STYLING_PROPS, TEXT_FIELD_STYLING_PROPS } from "@/constants/forms"
import { TestTemplateComparison } from "@/graphql/codegen/graphql"

interface ProductTestResultValueFieldProps {
  comparison: TestTemplateComparison
  index: number
}

export const ProductTestResultValueField: FC<ProductTestResultValueFieldProps> = ({ comparison, index }) => {
  const { control } = useFormContext<ProductTestFormValues>()

  const isTextInput = (comparison: TestTemplateComparison) => {
    return ![TestTemplateComparison.PassFail, TestTemplateComparison.YesNo].includes(comparison)
  }

  return (
    <Controller
      name={`results.${index}.value`}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          {isTextInput(comparison) && (
            <TextField {...field} {...TEXT_FIELD_STYLING_PROPS} error={!!error} data-test='result-value-input' />
          )}
          {comparison === TestTemplateComparison.PassFail && (
            <Select {...field} {...SELECT_STYLING_PROPS} error={!!error} data-test='result-value-pass-fail-select'>
              {Object.values(ProductTestResultPassFail).map((value) => (
                <MenuItem key={value} value={value}>
                  {getProductTestResultPassFailLabel(value)}
                </MenuItem>
              ))}
            </Select>
          )}
          {comparison === TestTemplateComparison.YesNo && (
            <Select {...field} {...SELECT_STYLING_PROPS} error={!!error} data-test='result-value-yes-no-select'>
              {Object.values(ProductTestResultYesNo).map((value) => (
                <MenuItem key={value} value={value}>
                  {getProductTestResultYesNoLabel(value)}
                </MenuItem>
              ))}
            </Select>
          )}
          {error?.message && (
            <p className={FIELD_ERROR_CLASSES} data-test='result-value-error'>
              {error.message}
            </p>
          )}
        </FormControl>
      )}
    />
  )
}
